/* import __COLOCATED_TEMPLATE__ from './publish-modal.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Component from '@glimmer/component';
import type ArticleContent from 'embercom/models/articles/article-content';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import { ScheduledContentUpdateStatus } from 'embercom/services/scheduled-content-update-service';
import type ScheduledContentUpdate from 'embercom/models/content-service/scheduled-content-update';
import { type ScheduleMode } from 'embercom/lib/knowledge-hub/constants';
import type AppService from 'embercom/services/app-service';
import type KnowledgeHubEditorService from 'embercom/services/knowledge-hub-editor-service';
import { type HelpCenterSelection } from './edit/help-center-modal';
import { NEW_SELECTOR } from './side-panel/help-center-selector';
import { type ScheduleSelection } from './schedule-picker';

enum ModalStep {
  WhereToPublish = 'where-to-publish',
  WhenToPublish = 'when-to-publish',
}

enum HelpCenterOption {
  InHelpCenter = 'in-help-center',
  UnlistedHelpCenter = 'unlisted-help-center',
}

interface Args {
  activeContent: ArticleContent;
  publishActiveContent: () => Promise<void>;
  // Optional: when omitted (e.g. content-review surface), the modal
  // hides the "Schedule for later" radio.
  scheduleActiveContentUpdate?: (params: {
    mode: ScheduleMode;
    scheduledDate: string;
    timezone?: string;
  }) => Promise<void>;
  onClose: () => void;
}

interface Signature {
  Args: Args;
}

export default class PublishModal extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare knowledgeHubEditorService: KnowledgeHubEditorService;
  @service declare helpCenterService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare scheduledContentUpdateService: $TSFixMe;

  @tracked currentStep: ModalStep = this.initialStep;
  @tracked helpCenterOption: HelpCenterOption = HelpCenterOption.InHelpCenter;
  @tracked declare schedule: ScheduleSelection;
  @tracked isPublishing = false;
  @tracked declare helpCenterSelection: HelpCenterSelection;
  @tracked modalHidden = false;

  // Captured at open-time: <HelpCenterSelector> mutates article.inCollections live, so the getter flips mid-flow.
  needsHelpCenterSelectionAtOpen = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.helpCenterSelection = { selectedTuples: undefined };
    this.needsHelpCenterSelectionAtOpen = this.needsHelpCenterSelection;
    let pending = this.pendingScheduledContentUpdate;
    this.schedule = pending
      ? {
          kind: 'schedule',
          scheduledDate: pending.changeAt.toISOString(),
          timezone: pending.timezone || this.appService.app.timezone,
        }
      : { kind: 'now' };
  }

  get initialStep(): ModalStep {
    if (this.needsHelpCenterSelection) {
      return ModalStep.WhereToPublish;
    }
    return ModalStep.WhenToPublish;
  }

  get isWhereStep(): boolean {
    return this.currentStep === ModalStep.WhereToPublish;
  }

  get isWhenStep(): boolean {
    return this.currentStep === ModalStep.WhenToPublish;
  }

  get article() {
    return this.args.activeContent.article;
  }

  get isScheduleMode(): boolean {
    return this.schedule.kind === 'schedule';
  }

  get needsHelpCenterSelection(): boolean {
    return this.article?.inCollections.length === 0 && this.args.activeContent.isNotPublished;
  }

  get hasSingleHelpCenter(): boolean {
    return this.helpCenterService.allSites.length === 1;
  }

  get shouldShowNotLiveStatus(): boolean {
    return this.hasSingleHelpCenter && !this.helpCenterService.allSites.firstObject.websiteTurnedOn;
  }

  get modalTitle() {
    return this.intl.t('knowledge-hub.content-editor.article-content.modal.publish-article');
  }

  get secondaryButtonLabel() {
    if (this.isWhenStep && this.needsHelpCenterSelectionAtOpen) {
      return this.intl.t('knowledge-hub.content-editor.article-content.publish-modal.back');
    }
    return this.intl.t('knowledge-hub.content-editor.article-content.buttons.cancel');
  }

  get canSchedule(): boolean {
    return !!this.args.scheduleActiveContentUpdate;
  }

  get pendingScheduledContentUpdates(): ScheduledContentUpdate[] {
    let updates = this.args.activeContent.scheduledContentUpdates ?? [];
    return updates.filter((update) => update.status === ScheduledContentUpdateStatus.PENDING);
  }

  get pendingScheduledContentUpdate(): ScheduledContentUpdate | undefined {
    return this.pendingScheduledContentUpdates[0];
  }

  get disableNextButton(): boolean {
    // On "where" step, disable Next when "In a Help Center" is selected but no collection chosen
    return (
      this.helpCenterOption === HelpCenterOption.InHelpCenter &&
      (this.helpCenterSelection.selectedTuples === undefined ||
        this.helpCenterSelection.selectedTuples?.size === 0 ||
        this.helpCenterSelection.selectedTuples?.has(NEW_SELECTOR))
    );
  }

  get isScheduledInPast(): boolean {
    return (
      this.schedule.kind === 'schedule' &&
      new Date(this.schedule.scheduledDate).getTime() <= Date.now()
    );
  }

  get confirmButtonLabel() {
    if (this.isWhereStep) {
      return this.intl.t('knowledge-hub.content-editor.article-content.publish-modal.next');
    }
    if (this.isScheduleMode) {
      return this.intl.t(
        'knowledge-hub.content-editor.article-content.publish-modal.confirm-schedule',
      );
    }
    return this.intl.t(
      'knowledge-hub.content-editor.article-content.publish-modal.confirm-publish',
    );
  }

  get disableConfirmButton(): boolean {
    if (this.isWhereStep) {
      return this.disableNextButton;
    }
    if (this.isScheduledInPast) {
      return true;
    }
    return false;
  }

  @action onScheduleChange(selection: ScheduleSelection) {
    this.schedule = selection;
  }

  @action updateHelpCenterSelection(selection: HelpCenterSelection) {
    this.helpCenterSelection = selection;
    let hasRealSelection =
      selection.selectedTuples !== undefined &&
      selection.selectedTuples.size > 0 &&
      !selection.selectedTuples.has(NEW_SELECTOR);
    if (hasRealSelection) {
      this.helpCenterOption = HelpCenterOption.InHelpCenter;
    }
  }

  @action setModalHidden(value: boolean) {
    this.modalHidden = value;
  }

  @action goToNextStep() {
    this.currentStep = ModalStep.WhenToPublish;
  }

  @action goBack() {
    this.currentStep = ModalStep.WhereToPublish;
  }

  @action async confirm() {
    // Step 1: advance to Step 2
    if (this.isWhereStep) {
      this.goToNextStep();
      return;
    }

    if (this.isScheduledInPast) {
      return;
    }

    // Persist collection choice if "In a Help Center" was selected at Step 1.
    // Applies to both publish-now and schedule paths — scheduling only stores
    // the trigger, so the collection must be saved on the article up front.
    if (
      this.helpCenterOption === HelpCenterOption.InHelpCenter &&
      this.needsHelpCenterSelectionAtOpen
    ) {
      await this.knowledgeHubEditorService.updateHelpCenterAndCollection(this.article);
    }

    // Step 2: execute publish or schedule
    if (this.isScheduleMode && this.args.scheduleActiveContentUpdate) {
      let { scheduledDate, timezone } = this.schedule as Extract<
        ScheduleSelection,
        { kind: 'schedule' }
      >;
      await this.args.scheduleActiveContentUpdate({
        mode: 'publish',
        scheduledDate,
        timezone,
      });
      this.args.onClose();
    } else {
      this.isPublishing = true;
      await this.args.publishActiveContent();
      this.isPublishing = false;
      this.args.onClose();
    }
  }

  @action cancel() {
    // Secondary button: on step 2 with a step 1, go back
    if (this.isWhenStep && this.needsHelpCenterSelectionAtOpen) {
      this.goBack();
      return;
    }
    this.closeModal();
  }

  @action closeModal() {
    // Clear collections if the help center section was shown
    if (this.needsHelpCenterSelectionAtOpen) {
      this.article?.inCollections.clear();
    }
    this.args.onClose();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::ArticleContent::PublishModal': typeof PublishModal;
    'knowledge-hub/content-editor/article-content/publish-modal': typeof PublishModal;
  }
}
